import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectContext } from '../contexts/ProjectContext';
import { useProject } from '../hooks/useProject';
import { useUser } from '../contexts/UserContext';
import MainLayout from '../components/Layout/MainLayout';
import MessageItem from '../components/Board/MessageItem';
import { useOnboardingStatus, type OnboardingStatus } from '../hooks/useOnboardingStatus';
import type { Message } from '../types/chat';

import chatStyles from '../assets/styles/components/ChatPanel.module.scss';
import diagnosisStyles from '../assets/styles/components/AiDiagnosisPanel.module.scss';
import suggestionStyles from '../assets/styles/components/AiSuggestionPanel.module.scss';
import boardStyles from '../assets/styles/Board.module.scss';
import styles from '../assets/styles/Onboarding.module.scss';

import { ReactComponent as ArrowWhiteIcon } from '../assets/icons/arrow-white.svg';

// ステップ表示用のコンポーネント
const StepItem = ({ stepNum, title, completed, isLast, children }: any) => (
  <div className={styles.stepItemWrap}>
    <div className={styles.stepIconCol}>
      <div
        className={`${styles.stepCircle} ${completed ? styles.stepCircleCompleted : styles.stepCircleIncomplete}`}
      >
        {completed ? '✓' : stepNum}
      </div>
      {!isLast && <div className={styles.stepLine}></div>}
    </div>
    <div
      className={`${styles.stepContent} ${isLast ? styles.stepContentLast : styles.stepContentNotLast}`}
    >
      <div
        className={`${styles.stepTitle} ${completed ? styles.stepTitleCompleted : styles.stepTitleIncomplete}`}
      >
        {title}
      </div>
      {children && (
        <div
          className={`${styles.stepChildren} ${completed ? styles.stepChildrenCompleted : styles.stepChildrenIncomplete}`}
        >
          {children}
        </div>
      )}
    </div>
  </div>
);

type SubStep =
  | 'GPT_API_KEY_INPUT'
  | 'GPT_ORGANIZATION_ID_INPUT'
  | 'PROJECT_NAME_INPUT'
  | 'URL_INPUT'
  | 'CONFIRM_NAME'
  | 'CONFIRM_OVERVIEW'
  | 'CONFIRM_PERSONA'
  | 'CONFIRM_MARKETING_FUNNEL'
  | 'CONFIRM_MARKETING_KPI'
  | 'CONFIRM_MARKETING_USP'
  | 'CONFIRM_MARKETING_NG'
  | 'CONFIRM_TECH_REQ_AREA'
  | 'CONFIRM_TECH_REQ_CPA'
  | 'CONFIRM_TECH_REQ_OTHER'
  | 'EXTERNAL_LINK'
  | 'BUDGET'
  | 'DONE';

type OnboardingOptionKey =
  | 'PRE_AWARENESS'
  | 'PROBLEM_AWARENESS'
  | 'INFORMATION_GATHERING'
  | 'COMPARISON'
  | 'CONVERSION'
  | 'EXISTING_CUSTOMER'
  | 'MAXIMIZE_CONVERSIONS'
  | 'IMPROVE_CPA'
  | 'MAXIMIZE_ROAS'
  | 'MAXIMIZE_CLICKS'
  | 'EXPAND_AWARENESS';

type OnboardingOption = {
  key: OnboardingOptionKey;
  label: string;
};

type OnboardingSubmissionIntent = 'FREE_TEXT' | 'CONFIRM_EXISTING_VALUE' | 'NO_WEBSITE';

type OnboardingAction =
  | { type: 'PREFILL_URL' }
  | {
    type: 'SET_TEXT';
    text: string;
    autoSubmit?: boolean;
    submissionIntent?: OnboardingSubmissionIntent;
  }
  | { type: 'TOGGLE_OPTION'; optionKey: OnboardingOptionKey }
  | { type: 'SUBMIT_CURRENT_INPUT' };

const MARKETING_FUNNEL_OPTIONS: OnboardingOption[] = [
  { key: 'PRE_AWARENESS', label: '認知前' },
  { key: 'PROBLEM_AWARENESS', label: '課題認知' },
  { key: 'INFORMATION_GATHERING', label: '情報収集' },
  { key: 'COMPARISON', label: '比較検討' },
  { key: 'CONVERSION', label: 'コンバージョン' },
  { key: 'EXISTING_CUSTOMER', label: '既存顧客' },
];

const MARKETING_KPI_OPTIONS: OnboardingOption[] = [
  { key: 'MAXIMIZE_CONVERSIONS', label: 'コンバージョン数の最大化' },
  { key: 'IMPROVE_CPA', label: 'CPAの改善' },
  { key: 'MAXIMIZE_ROAS', label: 'ROASの最大化' },
  { key: 'MAXIMIZE_CLICKS', label: 'クリック数の最大化' },
  { key: 'EXPAND_AWARENESS', label: '認知の拡大' },
];

const ONBOARDING_OPTION_LABEL_MAP: Record<OnboardingOptionKey, string> = {
  PRE_AWARENESS: '認知前',
  PROBLEM_AWARENESS: '課題認知',
  INFORMATION_GATHERING: '情報収集',
  COMPARISON: '比較検討',
  CONVERSION: 'コンバージョン',
  EXISTING_CUSTOMER: '既存顧客',
  MAXIMIZE_CONVERSIONS: 'コンバージョン数の最大化',
  IMPROVE_CPA: 'CPAの改善',
  MAXIMIZE_ROAS: 'ROASの最大化',
  MAXIMIZE_CLICKS: 'クリック数の最大化',
  EXPAND_AWARENESS: '認知の拡大',
};

const CONSULT_CONTEXT_MAP: Record<
  | 'CONFIRM_MARKETING_USP'
  | 'CONFIRM_MARKETING_NG'
  | 'CONFIRM_TECH_REQ_AREA'
  | 'CONFIRM_TECH_REQ_CPA'
  | 'CONFIRM_TECH_REQ_OTHER',
  string
> = {
  CONFIRM_MARKETING_USP: 'USP・訴求ポイント',
  CONFIRM_MARKETING_NG: 'NGワード',
  CONFIRM_TECH_REQ_AREA: '配信エリア',
  CONFIRM_TECH_REQ_CPA: '目標CPA',
  CONFIRM_TECH_REQ_OTHER: 'その他の条件',
};

const CONFIRMATION_SUB_STEPS: SubStep[] = ['CONFIRM_NAME', 'CONFIRM_OVERVIEW', 'CONFIRM_PERSONA'];

const CONSULTABLE_SUB_STEPS: SubStep[] = [
  'CONFIRM_MARKETING_USP',
  'CONFIRM_MARKETING_NG',
  'CONFIRM_TECH_REQ_AREA',
  'CONFIRM_TECH_REQ_CPA',
  'CONFIRM_TECH_REQ_OTHER',
];

type ConsultableSubStep = keyof typeof CONSULT_CONTEXT_MAP;

const isConsultableSubStep = (value: SubStep): value is ConsultableSubStep =>
  CONSULTABLE_SUB_STEPS.includes(value);

const OnboardingV2: React.FC = () => {
  const { user, fetchUser } = useUser();
  const {
    selectedProjectId,
    setSelectedProject,
    clearSelectedProject,
    isProjectReady,
    fetchProjects,
  } = useProjectContext();
  const { createProject, deleteProject } = useProject(user?.tenant_id);
  const navigate = useNavigate();

  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);

  const [subStep, setSubStep] = useState<SubStep>('URL_INPUT');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [areQuickActionsVisible, setAreQuickActionsVisible] = useState(true);
  const [submissionIntent, setSubmissionIntent] = useState<OnboardingSubmissionIntent>('FREE_TEXT');
  const [gptApiKey, setGptApiKey] = useState('');
  // 抽出されたデータ
  const [extractedData, setExtractedData] = useState<any>(null);
  const {
    status: fetchedOnboardingStatus,
    refetch: refetchOnboardingStatus,
  } = useOnboardingStatus(selectedProjectId);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const hasRunIntro = useRef(false);
  const isGptSetupRequired = user?.has_gpt_settings === false;

  useEffect(() => {
    setAreQuickActionsVisible(true);
  }, [subStep]);

  // Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isProcessing]);

  // Restore from localStorage on mount or projectId change
  useEffect(() => {
    // プロジェクト一覧の読み込み前はselectedProjectIdが一時的にnullになるため、
    // この時点で初回イントロを開始しない。
    if (!isProjectReady) return;

    if (selectedProjectId) {
      const localData = localStorage.getItem(`onboarding_state_${selectedProjectId}`);
      if (localData) {
        try {
          const parsed = JSON.parse(localData);
          if (parsed.messages && parsed.messages.length > 0) {
            // Remove animate: true so history messages don't re-animate
            const historyMsgs = parsed.messages.map((m: any) => ({ ...m, animate: false }));
            setMessages(historyMsgs);
            if (parsed.subStep) setSubStep(parsed.subStep);
            if (parsed.extractedData) setExtractedData(parsed.extractedData);
            if (isGptSetupRequired) {
              setSubStep('GPT_API_KEY_INPUT');
              if (!historyMsgs.some((m: Message) => m.text?.includes('GPTのAPIキー'))) {
                setMessages([
                  ...historyMsgs,
                  {
                    sender: 'bot',
                    text: 'AI機能を利用するためGPTと連携します。\nまずはAPI Keyを入力してください。',
                    animate: false,
                  },
                ]);
              }
            }
            if (parsed.onboardingStatus) {
              setOnboardingStatus((prev) => ({ ...prev, ...parsed.onboardingStatus }));
            }
            hasRunIntro.current = true;
            return;
          }
        } catch (e) {
          console.error('Failed to parse localStorage onboarding state', e);
        }
      }
    }

    // プロジェクト作成済みで保存済みの会話がない場合も、初期設定の先頭には戻さずURL入力から再開する。
    if (user && selectedProjectId && !isGptSetupRequired && !hasRunIntro.current) {
      hasRunIntro.current = true;
      setSubStep('URL_INPUT');
      setMessages([{
        sender: 'bot',
        text: 'まず初めに、広告を出稿したい公式サイトのURLを教えてください。',
        animate: false,
      }]);
      return;
    }

    if (user && messages.length === 0 && !isProcessing && !hasRunIntro.current) {
      hasRunIntro.current = true;
      if (!selectedProjectId && !isGptSetupRequired) {
        setSubStep('PROJECT_NAME_INPUT');
      }
      const runIntro = async () => {
        setIsProcessing(true);
        const introMsgs = [
          'Aibexへようこそ！',
          ...(selectedProjectId
            ? ['この度はAibexにご登録いただき、誠にありがとうございます。']
            : []),
          'それでは初期設定を進めていきましょう！',
          ...(isGptSetupRequired
            ? ['AI機能を利用するためGPTと連携します。\nまずはAPI Keyを入力してください。']
            : [
              selectedProjectId
                ? 'まず初めに、広告を出稿したい公式サイトのURLを教えてください。'
                : 'まず初めに、プロジェクト名を教えてください。',
            ]),
        ];

        if (isGptSetupRequired) {
          setSubStep('GPT_API_KEY_INPUT');
        }

        for (const text of introMsgs) {
          setMessages((prev) => [...prev, { sender: 'bot', isLoading: true, text: undefined }]);
          await new Promise((resolve) => setTimeout(resolve, 1200));

          setMessages((prev) => {
            const newMsgs = [...prev];
            // Replace the last element (which is the isLoading bubble) with the actual text
            newMsgs[newMsgs.length - 1] = { sender: 'bot', text, animate: true };
            return newMsgs;
          });

          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        setIsProcessing(false);
      };
      runIntro();
    }
  }, [isProjectReady, selectedProjectId, user?.has_gpt_settings]);

  // Auto-save to localStorage whenever state changes
  useEffect(() => {
    if (!selectedProjectId || messages.length === 0) return;
    if (onboardingStatus?.is_completed) {
      localStorage.removeItem(`onboarding_state_${selectedProjectId}`);
      return;
    }
    const stateToSave = {
      messages,
      subStep,
      onboardingStatus,
      extractedData,
    };
    localStorage.setItem(`onboarding_state_${selectedProjectId}`, JSON.stringify(stateToSave));
  }, [messages, subStep, onboardingStatus, extractedData, selectedProjectId]);

  useEffect(() => {
    if (!fetchedOnboardingStatus) return;
    setOnboardingStatus(fetchedOnboardingStatus);

    if (
      fetchedOnboardingStatus.is_ad_settings_done &&
      fetchedOnboardingStatus.is_ad_account_linked
    ) {
      navigate('/board');
      return;
    }

    if (
      fetchedOnboardingStatus.is_ad_settings_done &&
      !fetchedOnboardingStatus.is_ad_account_linked
    ) {
      setSubStep('EXTERNAL_LINK');
      if (!messages.some((message) => message.text?.includes('広告アカウントを連携'))) {
        addMessage({
          sender: 'bot',
          text: 'ここまでお疲れ様でした！最後に広告アカウントを連携すると、AI診断とチャットで広告データを分析できるようになります。\n\n以下のボタンから広告アカウントを連携してください。',
          animate: true,
        });
      }
    }
  }, [fetchedOnboardingStatus, messages, navigate]);

  const addMessage = (msg: Message) => setMessages((prev) => [...prev, msg]);

  const advanceToNextStep = async (nextSubStep: string, promptText: string) => {
    addMessage({ sender: 'bot', isLoading: true, text: undefined });
    // 少し長めの待機時間を設けて、「承知いたしました！」と次の質問が同時に表示されるのを防ぐ
    await new Promise((resolve) => setTimeout(resolve, 2500));
    setMessages((prev) => prev.filter((m) => !m.isLoading));
    addMessage({ sender: 'bot', text: promptText, animate: true });
    setSubStep(nextSubStep as any);
  };

  const skipToNextStep = (nextSubStep: SubStep, promptText: string) => {
    setMessages((prev) => [
      ...prev.filter((m) => !m.isLoading),
      { sender: 'bot', text: promptText, animate: true },
    ]);
    setSubStep(nextSubStep);
  };

  const handleLLMChat = async (text: string, currentStepStr?: string): Promise<boolean> => {
    setIsProcessing(true);
    addMessage({ sender: 'bot', isLoading: true, text: undefined });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/onboarding-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: selectedProjectId,
          message: text,
          chat_history: messages.map((m) => ({ sender: m.sender, text: m.text || '' })),
          extracted_data: extractedData || {},
          current_step: currentStepStr,
        }),
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => prev.filter((m) => !m.isLoading));
        addMessage({ sender: 'bot', text: data.reply, animate: true });
        const isAnswered = data.is_answered === true;
        if (!isAnswered) setAreQuickActionsVisible(true);
        return isAnswered;
      } else {
        setMessages((prev) => prev.filter((m) => !m.isLoading));
        addMessage({ sender: 'bot', text: 'エラーが発生しました。' });
        return false;
      }
    } catch (e) {
      setMessages((prev) => prev.filter((m) => !m.isLoading));
      addMessage({ sender: 'bot', text: '通信エラーが発生しました。' });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const completeOnboarding = async (finalMessages: Message[]) => {
    setIsProcessing(true);
    let success = true;
    addMessage({ sender: 'bot', isLoading: true, text: undefined });
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/extract-and-save-final-onboarding`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            project_id: selectedProjectId,
            chat_history: finalMessages.map((m) => ({ sender: m.sender, text: m.text || '' })),
          }),
          credentials: 'include',
        }
      );
      if (!res.ok) {
        success = false;
        setMessages((prev) => prev.filter((m) => !m.isLoading));
        addMessage({ sender: 'bot', text: '設定の保存に失敗しました。' });
      }
    } catch (e) {
      console.error(e);
      success = false;
      setMessages((prev) => prev.filter((m) => !m.isLoading));
      addMessage({ sender: 'bot', text: '通信エラーが発生しました。' });
    } finally {
      setIsProcessing(false);
      if (!success) return;

      setOnboardingStatus((prev) =>
        prev
          ? {
              ...prev,
              is_marketing_done: true,
              is_tech_req_done: true,
              is_ad_settings_done: true,
              is_ad_account_linked: false,
              is_completed: false,
            }
          : null
      );
      setMessages((prev) => prev.filter((m) => !m.isLoading));
      addMessage({
        sender: 'bot',
        text: 'ここまでお疲れ様でした！最後に広告アカウントを連携すると、AI診断とチャットで広告データを分析できるようになります。\n\n以下のボタンから広告アカウントを連携してください。',
        animate: true,
      });
      setSubStep('EXTERNAL_LINK');
    }
  };

  const saveGptSettings = async (apiKey: string, organizationId: string) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/setting-llm`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gpt_api_key: apiKey,
          gpt_organization_id: organizationId,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.status !== 'success') {
        addMessage({
          sender: 'bot',
          text: data?.message || 'GPT連携に失敗しました。入力内容を確認してください。',
        });
        return;
      }

      await fetchUser();
      setGptApiKey('');
      const nextStep: SubStep = selectedProjectId ? 'URL_INPUT' : 'PROJECT_NAME_INPUT';
      setSubStep(nextStep);
      addMessage({
        sender: 'bot',
        text: selectedProjectId
          ? 'GPT連携が完了しました。続いて、広告を出稿したい公式サイトのURLを教えてください。'
          : 'GPT連携が完了しました。続いて、プロジェクト名を教えてください。',
        animate: true,
      });
    } catch (error) {
      console.error('GPT設定保存エラー:', error);
      addMessage({
        sender: 'bot',
        text: 'GPT連携に失敗しました。時間をおいて再度お試しください。',
      });
    }
  };

  const restartOnboarding = async () => {
    if (!window.confirm('現在のプロジェクトと、これまでのオンボーディング内容を削除して最初からやり直しますか？\nこの操作は元に戻せません。')) {
      return;
    }

    setIsProcessing(true);
    const projectIdToDelete = selectedProjectId;
    const deleted = projectIdToDelete ? await deleteProject(projectIdToDelete) : true;
    if (!deleted) {
      setIsProcessing(false);
      window.alert('プロジェクトのリセットに失敗しました。時間をおいて再度お試しください。');
      return;
    }

    if (projectIdToDelete) {
      localStorage.removeItem(`onboarding_state_${projectIdToDelete}`);
    }
    clearSelectedProject();
    await fetchProjects();
    setMessages([]);
    setOnboardingStatus(null);
    setExtractedData(null);
    hasRunIntro.current = false;
    setSubStep(isGptSetupRequired ? 'GPT_API_KEY_INPUT' : 'PROJECT_NAME_INPUT');
    setIsProcessing(false);
  };

  const submitOnboardingStepFlow = async () => {
    if (
      !inputText.trim()
      || (subStep !== 'PROJECT_NAME_INPUT'
        && subStep !== 'GPT_API_KEY_INPUT'
        && subStep !== 'GPT_ORGANIZATION_ID_INPUT'
        && !selectedProjectId)
    ) return;
    const text = inputText.trim();
    const currentSubmissionIntent = submissionIntent;
    setInputText('');
    setSubmissionIntent('FREE_TEXT');
    setAreQuickActionsVisible(false);

    const isGptCredentialStep =
      subStep === 'GPT_API_KEY_INPUT' || subStep === 'GPT_ORGANIZATION_ID_INPUT';
    if (!isGptCredentialStep) {
      addMessage({ sender: 'user', text });
    }

    const confirmationSteps: SubStep[] = [
      'CONFIRM_NAME',
      'CONFIRM_OVERVIEW',
      'CONFIRM_PERSONA',
    ];
    if (confirmationSteps.includes(subStep) && !extractedData) {
      addMessage({
        sender: 'bot',
        text: '入力途中のデータを復元できなかったため、公式サイトのURLから再開します。',
      });
      setSubStep('URL_INPUT');
      return;
    }

    switch (subStep) {
      case 'GPT_API_KEY_INPUT':
        setGptApiKey(text);
        addMessage({
          sender: 'bot',
          text: '次にOrganization IDを入力してください。',
          animate: true,
        });
        setSubStep('GPT_ORGANIZATION_ID_INPUT');
        return;

      case 'GPT_ORGANIZATION_ID_INPUT':
        if (!gptApiKey.trim()) {
          addMessage({ sender: 'bot', text: '先にAPI Keyを入力してください。' });
          setSubStep('GPT_API_KEY_INPUT');
          return;
        }
        setIsProcessing(true);
        await saveGptSettings(gptApiKey.trim(), text);
        setIsProcessing(false);
        return;

      case 'PROJECT_NAME_INPUT': {
        setIsProcessing(true);
        addMessage({ sender: 'bot', isLoading: true, text: undefined });

        try {
          const result = await createProject(text);
          const createdProject = result?.data;

          if (result?.status !== 'success' || !createdProject?.id || !createdProject.project_name) {
            setMessages((prev) => prev.filter((m) => !m.isLoading));
            addMessage({ sender: 'bot', text: 'プロジェクトの作成に失敗しました。' });
            return;
          }

          await fetchProjects();
          setSelectedProject(createdProject.id, createdProject.project_name);
          setMessages((prev) => prev.filter((m) => !m.isLoading));
          addMessage({
            sender: 'bot',
            text: 'ありがとうございます。プロジェクトを作成しました。\n\n続いて、広告を出稿したい公式サイトのURLを教えてください。',
            animate: true,
          });
          setSubStep('URL_INPUT');
        } catch (error) {
          console.error('Onboarding project creation error:', error);
          setMessages((prev) => prev.filter((m) => !m.isLoading));
          addMessage({ sender: 'bot', text: 'プロジェクトの作成に失敗しました。' });
        } finally {
          setIsProcessing(false);
        }
        return;
      }

      case 'URL_INPUT': {
        setIsProcessing(true);
        addMessage({ sender: 'bot', isLoading: true, text: undefined });

        if (currentSubmissionIntent === 'NO_WEBSITE') {
          setExtractedData({
            service_name: '',
            service_overview: '',
            persona_insight: '',
            competitive_edge: '',
            cv_definition: '',
          });
          setMessages((prev) => prev.filter((m) => !m.isLoading));
          addMessage({
            sender: 'bot',
            text: '承知いたしました。それでは、宣伝したい「サービス名」を教えてください。',
            animate: true,
          });
          setSubStep('CONFIRM_NAME');
          setIsProcessing(false);
          return;
        }

        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/extract-url`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ project_id: selectedProjectId, url: text }),
            credentials: 'include',
          });

          if (res.ok) {
            const data = await res.json();
            setExtractedData(data.data);
            setMessages((prev) => prev.filter((m) => !m.isLoading));

            addMessage({
              sender: 'bot',
              text: `有難うございます！サービスについて理解できました。\nサービス名は「**${data.data.service_name || '不明'}**」でよろしいですか？`,
              animate: true,
            });
            setSubStep('CONFIRM_NAME');
          } else {
            setMessages((prev) => prev.filter((m) => !m.isLoading));
            addMessage({
              sender: 'bot',
              text: 'URLの解析に失敗しました。別のURLをお試しください。',
            });
          }
        } catch (e) {
          setMessages((prev) => prev.filter((m) => !m.isLoading));
          addMessage({ sender: 'bot', text: 'エラーが発生しました。' });
        } finally {
          setIsProcessing(false);
        }
        return;
      }

      case 'CONFIRM_NAME': {
        const finalName =
          currentSubmissionIntent === 'CONFIRM_EXISTING_VALUE' ? extractedData.service_name : text;
        if (currentSubmissionIntent !== 'CONFIRM_EXISTING_VALUE') {
          setExtractedData({ ...extractedData, service_name: finalName });
        }

        const nextOverview = extractedData.service_overview;
        const promptText = nextOverview
          ? `ありがとうございます。続いて、サービス概要は以下でよろしいですか？\n\n> ${nextOverview}`
          : `ありがとうございます。続いて、サービス概要（どんなサービスか）を教えてください。`;

        addMessage({
          sender: 'bot',
          text: promptText,
          animate: true,
        });
        setSubStep('CONFIRM_OVERVIEW');
        return;
      }

      case 'CONFIRM_OVERVIEW': {
        const finalOverview =
          currentSubmissionIntent === 'CONFIRM_EXISTING_VALUE'
            ? extractedData.service_overview
            : text;
        if (currentSubmissionIntent !== 'CONFIRM_EXISTING_VALUE') {
          setExtractedData({ ...extractedData, service_overview: finalOverview });
        }

        const nextPersona = extractedData.persona_insight;
        const promptText = nextPersona
          ? `ありがとうございます。次に、ターゲット属性（ペルソナ）は以下でよろしいですか？\n\n> ${nextPersona}`
          : `ありがとうございます。次に、主なターゲット層（どんな人向けか）を教えてください。`;

        addMessage({
          sender: 'bot',
          text: promptText,
          animate: true,
        });
        setSubStep('CONFIRM_PERSONA');
        return;
      }

      case 'CONFIRM_PERSONA': {
        let finalPersona: string;
        if (currentSubmissionIntent !== 'CONFIRM_EXISTING_VALUE') {
          const isAnswered = await handleLLMChat(text, 'ターゲット属性（ペルソナ）');
          if (!isAnswered) return;
          finalPersona = text;
          setExtractedData({ ...extractedData, persona_insight: finalPersona });
        } else {
          finalPersona = extractedData.persona_insight;
        }

        setIsProcessing(true);
        addMessage({ sender: 'bot', isLoading: true, text: undefined });

        try {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/save-persona`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              project_id: selectedProjectId,
              service_name: extractedData.service_name,
              service_overview: extractedData.service_overview,
              persona_insight: finalPersona,
              competitive_edge: extractedData.competitive_edge,
              cv_definition: extractedData.cv_definition,
              usp: extractedData.usp,
              thumb_rule: extractedData.thumb_rule,
              keep_ads_conditions: extractedData.keep_ads_conditions,
              legal_notes: extractedData.legal_notes,
            }),
            credentials: 'include',
          });

          await advanceToNextStep(
            'CONFIRM_MARKETING_FUNNEL',
            `ありがとうございます。いただいた内容で基本情報を保存しました！\n\n次に、より効果的な広告配信を行うために、マーケティング戦略についてお伺いします。\nまずは、今回のプロモーションで特に注力したい「検討フェーズ」はありますか？\n以下から該当するものをお知らせください（複数回答可）。\n・認知前\n・課題認知\n・情報収集\n・比較検討\n・コンバージョン\n・既存顧客\n\n> 意図：この情報をもとに、配信する媒体や入札戦略を最適化します。`
          );
        } catch (e) {
          setMessages((prev) => prev.filter((m) => !m.isLoading));
          addMessage({ sender: 'bot', text: '保存に失敗しました。' });
        } finally {
          setIsProcessing(false);
        }
        return;
      }

      case 'CONFIRM_MARKETING_FUNNEL': {
        const isAnswered = await handleLLMChat(
          text,
          '注力したい検討フェーズ（認知前、課題認知、情報収集、比較検討、コンバージョン、既存顧客）'
        );
        if (!isAnswered) return;

        await advanceToNextStep(
          'CONFIRM_MARKETING_KPI',
          `ありがとうございます。次に、「目標とするKPI」を教えてください。\n例: コンバージョン数の最大化、CPAの改善、ROASの最大化、クリック数の最大化、認知の拡大 など`
        );
        return;
      }

      case 'CONFIRM_MARKETING_KPI': {
        const isAnswered = await handleLLMChat(text, '目標とするKPI');
        if (!isAnswered) return;

        await advanceToNextStep(
          'CONFIRM_MARKETING_USP',
          `ありがとうございます。続いて、「USP（自社の強み・訴求ポイント）」があれば教えてください。\n例: 業界最安値、送料無料、サポート24時間対応、導入実績No.1 など`
        );
        return;
      }

      case 'CONFIRM_MARKETING_USP': {
        const isAnswered = await handleLLMChat(text, 'USP（自社の強み・訴求ポイント）');
        if (!isAnswered) return;

        await advanceToNextStep(
          'CONFIRM_MARKETING_NG',
          `ありがとうございます。最後に、「NGワード（広告で使ってはいけない禁止表現）」などがあれば教えてください。\n例: 「絶対」「No.1（根拠なし）」などの法律上の禁止表現や、ブランドイメージに合わないワード。（特になければ「なし」で構いません）`
        );
        return;
      }

      case 'CONFIRM_MARKETING_NG': {
        const isAnswered = await handleLLMChat(text, 'NGワード・禁止表現');
        if (!isAnswered) return;

        setOnboardingStatus((prev) => (prev ? { ...prev, is_marketing_done: true } : null));
        await advanceToNextStep(
          'CONFIRM_TECH_REQ_AREA',
          `ありがとうございます。次に、配信エリア（対象とする地域、または除外する地域）について教えてください。\n例: 東京都のみに配信、北海道と沖縄を除外 など\n\n> 意図：無駄なコストを抑え、狙ったターゲット層に確実にリーチするためです。`
        );
        return;
      }

      case 'CONFIRM_TECH_REQ_AREA': {
        const isAnswered = await handleLLMChat(text, '配信対象エリアや除外エリア');
        if (!isAnswered) return;

        await advanceToNextStep(
          'CONFIRM_TECH_REQ_CPA',
          `ありがとうございます。目標とするCPA（獲得単価）を教えてください。\n例: 10,000円`
        );
        return;
      }

      case 'CONFIRM_TECH_REQ_CPA': {
        const isAnswered = await handleLLMChat(text, '目標とするCPA（獲得単価）');
        if (!isAnswered) return;

        await advanceToNextStep(
          'CONFIRM_TECH_REQ_OTHER',
          `ありがとうございます。その他、法的注意点や季節性などの条件があれば教えてください。\n例: 「ボーナス期の12月と6月に売上が伸びる傾向がある」「薬機法に配慮した表現が必要」など。（特になければ「なし」で構いません）`
        );
        return;
      }

      case 'CONFIRM_TECH_REQ_OTHER': {
        const isAnswered = await handleLLMChat(text, '法的注意点や季節性などのその他の条件');
        if (!isAnswered) return;

        setOnboardingStatus((prev) => (prev ? { ...prev, is_ad_settings_done: true } : null));
        await completeOnboarding([...messages, { sender: 'user', text }]);
        return;
      }

      default:
        return;
    }
  };

  const handleOnboardingAction = (action: OnboardingAction) => {
    switch (action.type) {
      case 'PREFILL_URL':
        setInputText('https://');
        return;

      case 'TOGGLE_OPTION': {
        const optionLabel = ONBOARDING_OPTION_LABEL_MAP[action.optionKey];
        setInputText((prev) => {
          const items = prev
            .split('、')
            .map((s) => s.trim())
            .filter(Boolean);
          if (items.includes(optionLabel)) {
            return items.filter((i) => i !== optionLabel).join('、');
          }
          return [...items, optionLabel].join('、');
        });
        return;
      }

      case 'SUBMIT_CURRENT_INPUT':
        setAreQuickActionsVisible(false);
        if (!inputText.trim()) {
          const skipTargets: Partial<Record<SubStep, { next: SubStep; prompt: string }>> = {
            CONFIRM_MARKETING_FUNNEL: {
              next: 'CONFIRM_MARKETING_KPI',
              prompt: 'ありがとうございます。次に、「目標とするKPI」を教えてください。\n例: コンバージョン数の最大化、CPAの改善、ROASの最大化、クリック数の最大化、認知の拡大 など',
            },
            CONFIRM_MARKETING_KPI: {
              next: 'CONFIRM_MARKETING_USP',
              prompt: 'ありがとうございます。続いて、「USP（自社の強み・訴求ポイント）」があれば教えてください。\n例: 業界最安値、送料無料、サポート24時間対応、導入実績No.1 など',
            },
            CONFIRM_MARKETING_USP: {
              next: 'CONFIRM_MARKETING_NG',
              prompt: 'ありがとうございます。最後に、「NGワード（広告で使ってはいけない禁止表現）」などがあれば教えてください。\n例: 「絶対」「No.1（根拠なし）」などの法律上の禁止表現や、ブランドイメージに合わないワード。（特になければ「なし」で構いません）',
            },
            CONFIRM_MARKETING_NG: {
              next: 'CONFIRM_TECH_REQ_AREA',
              prompt: 'ありがとうございます。次に、配信エリア（対象とする地域、または除外する地域）について教えてください。\n例: 東京都のみに配信、北海道と沖縄を除外 など',
            },
            CONFIRM_TECH_REQ_AREA: {
              next: 'CONFIRM_TECH_REQ_CPA',
              prompt: 'ありがとうございます。目標とするCPA（獲得単価）を教えてください。\n例: 10,000円',
            },
            CONFIRM_TECH_REQ_CPA: {
              next: 'CONFIRM_TECH_REQ_OTHER',
              prompt: 'ありがとうございます。その他、法的注意点や季節性などの条件があれば教えてください。\n（特になければ「なし」で構いません）',
            },
          };
          const skipTarget = skipTargets[subStep];
          if (skipTarget) {
            if (subStep === 'CONFIRM_MARKETING_NG') {
              setOnboardingStatus((prev) => (prev ? { ...prev, is_marketing_done: true } : null));
            }
            skipToNextStep(skipTarget.next, skipTarget.prompt);
          } else if (subStep === 'CONFIRM_TECH_REQ_OTHER') {
            setOnboardingStatus((prev) => (prev ? { ...prev, is_ad_settings_done: true } : null));
            void completeOnboarding(messages);
          }
          return;
        }

        setSubmissionIntent('FREE_TEXT');
        setTimeout(() => {
          document.getElementById('onboarding-submit-btn')?.click();
        }, 50);
        return;

      case 'SET_TEXT':
        setInputText(action.text);
        setSubmissionIntent(action.submissionIntent ?? 'FREE_TEXT');
        if (action.autoSubmit) {
          setAreQuickActionsVisible(false);
          setTimeout(() => {
            document.getElementById('onboarding-submit-btn')?.click();
          }, 50);
        }
        return;

      default:
        return;
    }
  };

  const getPlaceholder = () => {
    switch (subStep) {
      case 'GPT_API_KEY_INPUT':
        return 'API Keyを入力...';
      case 'GPT_ORGANIZATION_ID_INPUT':
        return 'Organization IDを入力...';
      case 'PROJECT_NAME_INPUT':
        return 'プロジェクト名を入力...';
      case 'URL_INPUT':
        return '公式サイトのURLを入力...';
      case 'CONFIRM_NAME':
      case 'CONFIRM_OVERVIEW':
      case 'CONFIRM_PERSONA':
        return 'はい、または修正内容を入力...';
      default:
        return '入力してください...';
    }
  };

  const renderQuickActions = () => {
    if (isProcessing || !areQuickActionsVisible) return null;

    if (subStep === 'EXTERNAL_LINK') {
      return (
        <div className={`${chatStyles.followupWrap} ${chatStyles.visible} ${styles.followupWrapStart}`}>
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() => window.open('/project-setting/external', '_blank', 'noopener,noreferrer')}
          >
            広告アカウント連携設定を開く
          </button>
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() => void refetchOnboardingStatus()}
          >
            連携完了を確認
          </button>
        </div>
      );
    }

    if (subStep === 'URL_INPUT') {
      return (
        <div
          className={`${chatStyles.followupWrap} ${chatStyles.visible} ${styles.followupWrapStart}`}
        >
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() => handleOnboardingAction({ type: 'PREFILL_URL' })}
          >
            + https://
          </button>
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() =>
              handleOnboardingAction({
                type: 'SET_TEXT',
                text: '公式サイトはまだありません',
                autoSubmit: true,
                submissionIntent: 'NO_WEBSITE',
              })
            }
          >
            公式サイトはまだありません
          </button>
        </div>
      );
    }

    if (subStep === 'CONFIRM_MARKETING_FUNNEL') {
      return (
        <div
          className={`${chatStyles.followupWrap} ${chatStyles.visible} ${styles.followupWrapStart}`}
        >
          {MARKETING_FUNNEL_OPTIONS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={chatStyles.followupChip}
              onClick={() => handleOnboardingAction({ type: 'TOGGLE_OPTION', optionKey: item.key })}
            >
              + {item.label}
            </button>
          ))}
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() => handleOnboardingAction({ type: 'SUBMIT_CURRENT_INPUT' })}
          >
            次へ進む
          </button>
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() =>
              handleOnboardingAction({ type: 'SET_TEXT', text: 'AIに相談したい', autoSubmit: true })
            }
          >
            AIに相談したい
          </button>
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() =>
              handleOnboardingAction({ type: 'SET_TEXT', text: 'なし', autoSubmit: true })
            }
          >
            なし
          </button>
        </div>
      );
    }

    if (subStep === 'CONFIRM_MARKETING_KPI') {
      return (
        <div
          className={`${chatStyles.followupWrap} ${chatStyles.visible} ${styles.followupWrapStart}`}
        >
          {MARKETING_KPI_OPTIONS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={chatStyles.followupChip}
              onClick={() => handleOnboardingAction({ type: 'TOGGLE_OPTION', optionKey: item.key })}
            >
              + {item.label}
            </button>
          ))}
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() => handleOnboardingAction({ type: 'SUBMIT_CURRENT_INPUT' })}
          >
            次へ進む
          </button>
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() =>
              handleOnboardingAction({ type: 'SET_TEXT', text: 'AIに相談したい', autoSubmit: true })
            }
          >
            AIに相談したい
          </button>
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() =>
              handleOnboardingAction({ type: 'SET_TEXT', text: 'なし', autoSubmit: true })
            }
          >
            なし
          </button>
        </div>
      );
    }

    if (isConsultableSubStep(subStep)) {
      return (
        <div
          className={`${chatStyles.followupWrap} ${chatStyles.visible} ${styles.followupWrapStart}`}
        >
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() => handleOnboardingAction({ type: 'SUBMIT_CURRENT_INPUT' })}
          >
            次へ進む
          </button>
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() =>
              handleOnboardingAction({
                type: 'SET_TEXT',
                text: `AIに相談したい（${CONSULT_CONTEXT_MAP[subStep]}について）`,
                autoSubmit: true,
              })
            }
          >
            AIに相談したい
          </button>
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() =>
              handleOnboardingAction({ type: 'SET_TEXT', text: 'なし', autoSubmit: true })
            }
          >
            なし
          </button>
        </div>
      );
    }

    if (CONFIRMATION_SUB_STEPS.includes(subStep)) {
      return (
        <div className={`${chatStyles.followupWrap} ${chatStyles.visible}`}>
          <button
            type="button"
            className={chatStyles.followupChip}
            onClick={() =>
              handleOnboardingAction({
                type: 'SET_TEXT',
                text: 'はい、問題ありません',
                autoSubmit: true,
                submissionIntent: 'CONFIRM_EXISTING_VALUE',
              })
            }
          >
            はい、問題ありません
          </button>
        </div>
      );
    }

    return null;
  };

  // 左カラム: AI診断のタブとメッセージ
  const aiDiagnosisPanel = (
    <div className={diagnosisStyles.diagnosisPanel}>
      <div className={diagnosisStyles.panelHeader}>
        <span>AI診断</span>
      </div>
      <div className={diagnosisStyles.diagnosisFilterBar}>
        <button type="button" className={`${diagnosisStyles.filterTab} ${diagnosisStyles.active}`}>
          すべて
        </button>
        <button type="button" className={diagnosisStyles.filterTab}>
          緊急
        </button>
        <button type="button" className={diagnosisStyles.filterTab}>
          注意
        </button>
        <button type="button" className={diagnosisStyles.filterTab}>
          重要
        </button>
      </div>
      <div className={diagnosisStyles.diagnosisContent}>
        <div className={`${diagnosisStyles.emptyText} ${styles.emptyTextCenter}`}>
          {/* Diagnostic items would render here */}
          AIがプロジェクト状況を分析し、
          <br />
          必要なアクションを提案します。
        </div>
      </div>
    </div>
  );

  // 右カラム: 初期設定の進捗
  // GPT連携は完了済みでも、進捗一覧には完了ステップとして残す。
  const onboardingStepOffset = 1;
  const gptApiKeyStepCompleted = Boolean(user?.has_gpt_settings || gptApiKey.trim());
  const gptOrganizationStepCompleted = Boolean(user?.has_gpt_settings);
  const aiSuggestionPanel = (
    <div className={suggestionStyles.suggestionWrap}>
      <div className={`${suggestionStyles.suggestionContent} ${styles.suggestionContentPadded}`}>
        <StepItem stepNum={1} title="GPT連携" completed={Boolean(user?.has_gpt_settings)}>
          <ul className={styles.stepUl}>
            <li className={gptApiKeyStepCompleted ? styles.stepLiCompleted : styles.stepLi}>
              API Key入力
            </li>
            <li
              className={
                gptOrganizationStepCompleted ? styles.stepLiCompleted : styles.stepLi
              }
            >
              Organization ID入力
            </li>
          </ul>
        </StepItem>
        <StepItem
          stepNum={1 + onboardingStepOffset}
          title="プロジェクトの作成"
          completed={Boolean(selectedProjectId)}
        >
          <ul className={styles.stepUl}>
            <li className={selectedProjectId ? styles.stepLiCompleted : styles.stepLi}>
              プロジェクト名の決定
            </li>
          </ul>
        </StepItem>
        <StepItem
          stepNum={2 + onboardingStepOffset}
          title="広告基本設定"
          completed={onboardingStatus?.is_ad_settings_done}
        >
          <ul className={styles.stepUl}>
            <li
              className={
                onboardingStatus?.is_basic_info_done ? styles.stepLiCompleted : styles.stepLi
              }
            >
              プロジェクトの基本情報
            </li>
            <li
              className={
                onboardingStatus?.is_marketing_done ? styles.stepLiCompleted : styles.stepLi
              }
            >
              マーケティングコンテキスト
            </li>
            <li
              className={
                onboardingStatus?.is_tech_req_done ? styles.stepLiCompleted : styles.stepLi
              }
            >
              制約事項・技術的要件
            </li>
          </ul>
        </StepItem>
        <StepItem
          stepNum={3 + onboardingStepOffset}
          title="予算・目標設定"
          completed={onboardingStatus?.is_budget_done}
        >
          <ul className={styles.stepUl}>
            <li
              className={onboardingStatus?.is_budget_done ? styles.stepLiCompleted : styles.stepLi}
            >
              期間別予算
            </li>
            <li
              className={onboardingStatus?.is_budget_done ? styles.stepLiCompleted : styles.stepLi}
            >
              目標CPA/ROAS
            </li>
          </ul>
        </StepItem>
        <StepItem
          stepNum={4 + onboardingStepOffset}
          title="外部データ連携"
          completed={onboardingStatus?.is_external_link_done}
        >
          <ul className={styles.stepUl}>
            <li
              className={
                onboardingStatus?.is_external_link_done ? styles.stepLiCompleted : styles.stepLi
              }
            >
              Googleアカウント
            </li>
            <li
              className={
                onboardingStatus?.is_external_link_done ? styles.stepLiCompleted : styles.stepLi
              }
            >
              Metaアカウント
            </li>
            <li
              className={
                onboardingStatus?.is_external_link_done ? styles.stepLiCompleted : styles.stepLi
              }
            >
              Google Analytics 4
            </li>
          </ul>
        </StepItem>
        <StepItem
          stepNum={5 + onboardingStepOffset}
          title="広告アカウント連携"
          completed={onboardingStatus?.is_ad_account_linked}
          isLast={true}
        >
          <ul className={styles.stepUl}>
            <li
              className={
                onboardingStatus?.is_ad_account_linked ? styles.stepLiCompleted : styles.stepLi
              }
            >
              Google広告・Meta広告・Yahoo広告のいずれか1つ以上
            </li>
          </ul>
        </StepItem>
      </div>
    </div>
  );

  // 中央カラム: チャット画面
  const chatPanel = (
    <div className={chatStyles.chatPanel}>
      <div className={`${chatStyles.chatHeader} ${styles.chatHeaderRelative}`}>
        <span className={chatStyles.chatTitle}>初期設定</span>
        <button
          onClick={restartOnboarding}
          className={styles.resetBtn}
        >
          最初からやり直す
        </button>
      </div>

      <div className={`${chatStyles.chatContent} ${styles.chatContentSpaced}`}>
        {messages.map((msg, idx) => {
          const isConsecutive = idx > 0 && messages[idx - 1].sender === msg.sender;
          return (
            <MessageItem
              key={idx}
              msg={msg}
              isOnboarding={true}
              isConsecutive={isConsecutive}
              hideIcon={isConsecutive}
            />
          );
        })}
        <div ref={chatEndRef} />
      </div>

      <div className={chatStyles.inputArea}>
        {renderQuickActions()}
        {subStep !== 'EXTERNAL_LINK' && (
          <div className={chatStyles.inputAreaInner}>
            <textarea
              id="onboarding-chat-input-area"
              className={`${chatStyles.chatInput} ${styles.textAreaRestricted}`}
              placeholder={getPlaceholder()}
              value={inputText}
              onChange={(e) => {
                setInputText(e.target.value);
                e.target.style.height = '36px';
                e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing && !e.shiftKey) {
                  e.preventDefault();
                  submitOnboardingStepFlow();
                }
              }}
              disabled={isProcessing}
            />
            <button
              id="onboarding-submit-btn"
              type="button"
              className={chatStyles.sendButton}
              onClick={submitOnboardingStepFlow}
              disabled={isProcessing}
            >
              <ArrowWhiteIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <MainLayout fullBleed>
      <div className={`${boardStyles.contentArea} ${boardStyles.contentAreaWithSuggestion}`}>
        <div className={boardStyles.diagnosisPanel}>{aiDiagnosisPanel}</div>
        <div className={boardStyles.chatPanel}>{chatPanel}</div>
        <div className={boardStyles.suggestionPanel}>{aiSuggestionPanel}</div>
      </div>
    </MainLayout>
  );
};

export default OnboardingV2;
